import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useData, useList } from '@yingbaba/api';
import type {
  AdminAccountRow,
  AdminOrganizationRow,
  AdminOrganizationDetail,
  AdminOrganizationMember,
  AdminOrganizationRecord,
} from '@yingbaba/types';
import {
  Avatar,
  Button,
  PageTitle,
  Select,
  Status,
  QueryState,
  Pagination,
  Notice,
  Stat,
} from '@yingbaba/ui';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import { money, siteURL } from '@yingbaba/utils';
import { PortfolioReview } from './PortfolioReview';
import './directory.css';

const pageSize = 20;
const directoryDate = (value: string) =>
  new Date(value).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
const roles: Record<string, string> = {
  user: '普通用户',
  super_admin: '最高管理员',
  operator: '运营人员',
  reviewer: '审核人员',
  finance: '财务人员',
  customer_service: '客服人员',
  auditor: '审计人员',
};
const capabilities: Record<string, string> = {
  client: '采购方',
  provider: '服务商',
  resource: '资源方',
};
const verificationLabels: Record<string, string> = {
  unverified: '未认证',
  pending_review: '审核中',
  verified: '已认证',
  rejected: '未通过',
  revoked: '已撤销',
  materials_required: '待完善认证资料',
};
const providerLabels: Record<string, string> = {
  draft: '未提交',
  pending_review: '待审核',
  verified: '已通过',
  rejected: '未通过',
};
const subjectLabel = (value: string) =>
  ({ individual: '个人主体', enterprise: '企业主体' })[value] || '尚未选择';
function Certification({
  value,
  materialsRequired,
}: {
  value: string;
  materialsRequired?: boolean;
}) {
  if (materialsRequired) return <Status value="unverified" label="待完善认证资料" />;
  return <Status value={value || 'unverified'} label={verificationLabels[value || 'unverified']} />;
}
function Capabilities({ value }: { value: string }) {
  return (
    <div className="tags">
      {value
        .split(',')
        .filter(Boolean)
        .map((v) => (
          <span className="tag" key={v}>
            {capabilities[v] || v}
          </span>
        ))}
    </div>
  );
}
function useDirectoryParams() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const requestedPage = Number(params.get('page'));
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  function change(values: Record<string, string>, resetPage = true) {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (resetPage) next.delete('page');
        for (const [key, value] of Object.entries(values)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { state: location.state },
    );
  }
  const query = new URLSearchParams(params);
  query.set('page', String(page));
  query.set('page_size', String(pageSize));
  return { params, page, query, change };
}
function DirectorySearch({
  value,
  label,
  onSearch,
}: {
  value: string;
  label: string;
  onSearch: (value: string) => void;
}) {
  return (
    <form
      className="directory-search"
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch(String(new FormData(event.currentTarget).get('q') || '').trim());
      }}
    >
      <input
        className="input"
        type="search"
        key={value}
        defaultValue={value}
        name="q"
        aria-label={label}
        placeholder={label}
      />
      <Button variant="secondary">搜索</Button>
    </form>
  );
}
export function AccountsDirectory() {
  const { params, query, page, change } = useDirectoryParams();
  const list = useList<AdminAccountRow>(`/admin/accounts?${query}`);
  return (
    <>
      <PageTitle
        title="用户管理"
        description="查找平台账号，了解账号验证状态与所属组织。平台权限在「运营人员」中管理。"
      />
      <div className="filter-bar directory-filters">
        <DirectorySearch
          value={params.get('q') || ''}
          label="搜索姓名、邮箱或用户 ID"
          onSearch={(q) => change({ q })}
        />
        <Select
          aria-label="平台角色"
          value={params.get('role') || ''}
          onChange={(role) => change({ role })}
        >
          <option value="">全部平台角色</option>
          {Object.entries(roles).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </Select>
        <span className="result-count">共 {list.data?.total ?? '…'} 位用户</span>
      </div>
      <QueryState query={list} empty={!list.data?.items.length}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {['用户', '平台角色', '账号验证', '所属组织', '注册时间'].map((v) => (
                  <th key={v}>{v}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="directory-person">
                      <Avatar name={item.name} src={item.avatar_url} size={36} />
                      <div>
                        <strong>{item.name}</strong>
                        <span className="sub-cell">{item.email}</span>
                        <span className="sub-cell">ID {item.id}</span>
                      </div>
                    </div>
                  </td>
                  <td>{roles[item.platform_role] || item.platform_role}</td>
                  <td>
                    {item.email_verified_at ? '邮箱已验证' : '邮箱未验证'}
                    <span className="sub-cell">
                      {item.mfa_enabled ? '已启用双重验证' : '未启用双重验证'}
                    </span>
                  </td>
                  <td>
                    {item.organization_count ? (
                      <Link className="directory-link" to={`/organizations?account_id=${item.id}`}>
                        {item.organization_count} 个组织
                      </Link>
                    ) : (
                      '尚未加入组织'
                    )}
                  </td>
                  <td>{directoryDate(item.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
      <Pagination
        page={page}
        size={pageSize}
        total={list.data?.total || 0}
        onPage={(n) => change({ page: String(n) }, false)}
      />
    </>
  );
}
export function OrganizationsDirectory() {
  const { params, query, page, change } = useDirectoryParams();
  const list = useList<AdminOrganizationRow>(`/admin/organizations?${query}`);
  return (
    <>
      <PageTitle
        title="组织管理"
        description="按主体认证与业务身份查找组织，查看成员、内容和项目情况。"
      />
      {params.get('account_id') && (
        <Notice>
          正在查看指定用户所属的组织。
          <Link className="directory-link" to="/organizations">
            查看全部组织
          </Link>
        </Notice>
      )}
      <div className="filter-bar directory-filters">
        <DirectorySearch
          value={params.get('q') || ''}
          label="搜索组织名称、城市或组织 ID"
          onSearch={(q) => change({ q })}
        />
        <Select
          aria-label="主体认证状态"
          value={params.get('status') || ''}
          onChange={(status) => change({ status })}
        >
          <option value="">全部认证状态</option>
          {Object.entries(verificationLabels).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="业务身份"
          value={params.get('capability') || ''}
          onChange={(capability) => change({ capability })}
        >
          <option value="">全部业务身份</option>
          {Object.entries(capabilities).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="主体类型"
          value={params.get('subject_type') || ''}
          onChange={(subject_type) => change({ subject_type })}
        >
          <option value="">全部主体类型</option>
          <option value="individual">个人主体</option>
          <option value="enterprise">企业主体</option>
        </Select>
        <span className="result-count">共 {list.data?.total ?? '…'} 个组织</span>
      </div>
      <QueryState query={list} empty={!list.data?.items.length}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {[
                  '组织',
                  '业务身份',
                  '主体认证',
                  '服务商审核',
                  '负责人 / 成员',
                  '创建时间',
                  '详情',
                ].map((v) => (
                  <th key={v}>{v}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="directory-person">
                      <Avatar name={item.name} src={item.avatar_url} size={40} organization />
                      <div>
                        <Link
                          className="directory-link title-cell"
                          to={`/organizations/${item.id}`}
                          state={{ directorySearch: params.toString() }}
                        >
                          {item.name}
                        </Link>
                        <span className="sub-cell">
                          {item.city || '城市未填写'} · {subjectLabel(item.subject_type)}
                        </span>
                        <span className="sub-cell">ID {item.id}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <Capabilities value={item.capabilities} />
                  </td>
                  <td>
                    <Certification
                      value={item.verification_status}
                      materialsRequired={item.verification_materials_required}
                    />
                  </td>
                  <td>
                    {item.provider_status ? (
                      <Status
                        value={item.provider_status}
                        label={providerLabels[item.provider_status]}
                      />
                    ) : (
                      '未创建服务商资料'
                    )}
                  </td>
                  <td>
                    {item.owner_name || '暂无负责人'}
                    <span className="sub-cell">{item.member_count} 位成员</span>
                  </td>
                  <td>{directoryDate(item.created_at)}</td>
                  <td>
                    <Link
                      className="btn btn-secondary btn-sm"
                      to={`/organizations/${item.id}`}
                      state={{ directorySearch: params.toString() }}
                    >
                      查看详情
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
      <Pagination
        page={page}
        size={pageSize}
        total={list.data?.total || 0}
        onPage={(n) => change({ page: String(n) }, false)}
      />
    </>
  );
}

const tabs = {
  overview: '组织概览',
  members: '组织成员',
  content: '发布内容',
  business: '项目与交易',
};
export function OrganizationDetail() {
  const { organizationID = '' } = useParams();
  const { state } = useLocation();
  const directorySearch =
    typeof state?.directorySearch === 'string'
      ? new URLSearchParams(state.directorySearch).toString()
      : '';
  const { params, change } = useDirectoryParams();
  const tab = params.get('tab') || 'overview';
  const query = useData<AdminOrganizationDetail>(`/admin/organizations/${organizationID}`);
  const data = query.data;
  return (
    <div className="organization-detail">
      <Link
        className="directory-back"
        to={`/organizations${directorySearch ? '?' + directorySearch : ''}`}
      >
        <ArrowLeft size={15} />
        组织管理
      </Link>
      <QueryState query={query}>
        {data && (
          <>
            <div className="organization-heading">
              <Avatar
                name={data.organization.name}
                src={data.organization.avatar_url}
                size={64}
                organization
              />
              <div>
                <h1>{data.organization.name}</h1>
                <p className="muted small">
                  {data.organization.city || '城市未填写'} ·{' '}
                  {subjectLabel(data.organization.subject_type)} · 创建于{' '}
                  {directoryDate(data.organization.created_at)}
                </p>
                <p className="muted small organization-id">组织 ID：{data.organization.id}</p>
                <div className="row wrap">
                  <Certification
                    value={data.organization.verification_status}
                    materialsRequired={data.organization.verification_materials_required}
                  />
                  <Capabilities value={data.organization.capabilities} />
                </div>
              </div>
            </div>
            <div className="stats organization-stats">
              <Stat label="组织成员" value={data.counts.members} />
              <Stat label="拍片需求" value={data.counts.demands} />
              <Stat label="影视资源" value={data.counts.resources} />
              <Stat label="作品案例" value={data.counts.cases} />
              <Stat label="服务产品" value={data.counts.service_packages} />
              <Stat
                label="参与项目"
                value={data.counts.projects}
                foot={`${data.counts.active_projects} 个进行中`}
              />
            </div>
            <nav className="tabs" aria-label="组织详情分类">
              {Object.entries(tabs).map(([key, label]) => (
                <Link
                  key={key}
                  to={`?tab=${key}`}
                  state={state}
                  className={tab === key ? 'active' : ''}
                  aria-current={tab === key ? 'page' : undefined}
                >
                  {label}
                </Link>
              ))}
            </nav>
            {!Object.hasOwn(tabs, tab) ? (
              <Notice>
                未找到此详情分类。<Link to="?tab=overview">返回组织概览</Link>
              </Notice>
            ) : tab === 'overview' ? (
              <OrganizationOverview data={data} />
            ) : tab === 'members' ? (
              <OrganizationMembers key={organizationID} id={organizationID} />
            ) : (
              <OrganizationRecords
                key={`${organizationID}-${tab}`}
                id={organizationID}
                business={tab === 'business'}
                canViewOrders={data.can_view_orders}
                verified={data.organization.verification_status === 'verified'}
                kind={params.get('kind') || (tab === 'business' ? 'projects' : 'demands')}
                onKind={(kind) => change({ kind })}
              />
            )}
          </>
        )}
      </QueryState>
    </div>
  );
}
function OrganizationOverview({ data }: { data: AdminOrganizationDetail }) {
  const { organization: org, verification, provider } = data;
  return (
    <div className="organization-overview">
      <section className="panel stack">
        <h2>主体认证</h2>
        <div className="row wrap">
          <Certification
            value={org.verification_status}
            materialsRequired={org.verification_materials_required}
          />
          <span className="muted small">{subjectLabel(org.subject_type)}</span>
        </div>
        {org.verification_materials_required && (
          <Notice>
            当前组织缺少与主体一致的有效认证记录。请联系组织所有者完善资料并提交审核，组织标记不能代替正式认证。
          </Notice>
        )}
        {verification ? (
          <>
            <dl className="organization-facts">
              <div>
                <dt>最近提交</dt>
                <dd>
                  {verification.submitted_at ? directoryDate(verification.submitted_at) : '—'}
                </dd>
              </div>
              <div>
                <dt>认证通过</dt>
                <dd>{org.verified_at ? directoryDate(org.verified_at) : '尚未通过'}</dd>
              </div>
            </dl>
            {verification.review_reason && (
              <p className="small prose">审核意见：{verification.review_reason}</p>
            )}
            <Link
              className="btn btn-secondary"
              to={`/organization-verifications?organization_id=${org.id}`}
            >
              查看认证记录
            </Link>
          </>
        ) : (
          <p className="muted small">暂无可查看的主体认证申请记录。未提交的草稿由组织自行维护。</p>
        )}
        <p className="muted small">
          主体认证有效后，组织才能公开发布、参与交易与收款。证件材料在认证审核页按权限查看，并记录访问审计。
        </p>
      </section>
      <section className="panel stack">
        <h2>服务商资料</h2>
        {provider ? (
          <>
            <div className="row wrap">
              <strong>{provider.name}</strong>
              <Status value={provider.status} label={providerLabels[provider.status]} />
            </div>
            <p className="muted small">{provider.category || '分类未填写'}</p>
            <p className="small prose">{provider.summary || '简介未填写'}</p>
            {provider.review_reason && (
              <p className="small prose">审核意见：{provider.review_reason}</p>
            )}
            <div className="row wrap">
              <Link className="btn btn-secondary" to={`/providers?organization_id=${org.id}`}>
                查看服务商资料
              </Link>
              {org.verification_status === 'verified' && provider.status === 'verified' && (
                <a
                  className="btn btn-ghost"
                  href={siteURL('www', `/providers/${provider.id}`)}
                  target="_blank"
                  rel="noreferrer"
                >
                  公开主页
                  <ArrowUpRight size={15} />
                </a>
              )}
            </div>
          </>
        ) : (
          <p className="muted small">
            尚未创建服务商资料。采购方和仅发布资源的组织不一定需要服务商主页。
          </p>
        )}
      </section>
    </div>
  );
}
function OrganizationMembers({ id }: { id: string }) {
  const { page, change } = useDirectoryParams();
  const list = useList<AdminOrganizationMember>(
    `/admin/organizations/${id}/members?page=${page}&page_size=${pageSize}`,
  );
  return (
    <>
      <p className="muted small directory-intro">
        共 {list.data?.total ?? '…'} 位成员。此处角色为组织内部权限，与平台运营角色分开管理。
      </p>
      <QueryState query={list} empty={!list.data?.items.length}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {['成员', '组织角色', '加入时间', '账号'].map((v) => (
                  <th key={v}>{v}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="directory-person">
                      <Avatar name={item.name} src={item.avatar_url} size={36} />
                      <div>
                        <strong>{item.name}</strong>
                        <span className="sub-cell">{item.email}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    {{
                      owner: '负责人',
                      admin: '管理员',
                      member: '成员',
                      finance: '财务',
                      viewer: '只读成员',
                    }[item.role] || item.role}
                  </td>
                  <td>{directoryDate(item.created_at)}</td>
                  <td>
                    <Link
                      className="directory-link"
                      to={`/accounts?q=${encodeURIComponent(item.email)}`}
                    >
                      查看用户
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
      <Pagination
        page={page}
        size={pageSize}
        total={list.data?.total || 0}
        onPage={(n) => change({ page: String(n) }, false)}
      />
    </>
  );
}
function OrganizationRecords({
  id,
  business,
  canViewOrders,
  verified,
  kind,
  onKind,
}: {
  id: string;
  business: boolean;
  canViewOrders: boolean;
  verified: boolean;
  kind: string;
  onKind: (kind: string) => void;
}) {
  const { page, change } = useDirectoryParams();
  const options: Record<string, string> = business
    ? { projects: '参与项目', ...(canViewOrders ? { orders: '交易订单' } : {}) }
    : {
        demands: '拍片需求',
        resources: '影视资源',
        cases: '作品案例',
        'service-packages': '服务产品',
      };
  const allowed = Object.hasOwn(options, kind);
  const list = useList<AdminOrganizationRecord>(
    `/admin/organizations/${id}/records?kind=${kind}&page=${page}&page_size=${pageSize}`,
    allowed,
  );
  if (!allowed)
    return (
      <Notice>
        当前分类不可查看。
        <Button variant="secondary" onClick={() => onKind(business ? 'projects' : 'demands')}>
          返回{business ? '项目' : '需求'}列表
        </Button>
      </Notice>
    );
  return (
    <>
      <div className="filter-bar directory-filters">
        <Select
          aria-label={business ? '业务记录类型' : '发布内容类型'}
          value={kind}
          onChange={onKind}
        >
          {Object.entries(options).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </Select>
        <span className="result-count">共 {list.data?.total ?? '…'} 条</span>
        {kind !== 'service-packages' && (
          <Link className="directory-link" to={`/${kind}?organization_id=${id}`}>
            进入{options[kind]}管理
          </Link>
        )}
      </div>
      {business && !canViewOrders && (
        <p className="muted small directory-intro">
          当前角色可查看项目情况；订单金额与财务记录由授权人员查看。
        </p>
      )}
      <QueryState query={list} empty={!list.data?.items.length}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{options[kind]}</th>
                <th>分类</th>
                {business && <th>参与身份</th>}
                {kind === 'orders' && <th>订单金额</th>}
                <th>状态</th>
                <th>创建时间</th>
                <th>查看</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong className="title-cell">{item.title || '未命名记录'}</strong>
                    <span className="sub-cell">ID {item.id}</span>
                  </td>
                  <td>{item.category || '—'}</td>
                  {business && (
                    <td>
                      {{ client: '采购方', provider: '服务商', both: '双方' }[item.participation] ||
                        '—'}
                    </td>
                  )}
                  {kind === 'orders' && (
                    <td>{item.amount_minor === undefined ? '—' : money(item.amount_minor)}</td>
                  )}
                  <td>
                    <Status value={item.status} />
                  </td>
                  <td>{directoryDate(item.created_at)}</td>
                  <td>
                    <div className="table-actions">
                      {kind === 'cases' && <PortfolioReview id={item.id} />}
                      {['demands', 'resources', 'cases'].includes(kind) &&
                        verified &&
                        item.status === 'published' && (
                          <a
                            className="directory-link"
                            target="_blank"
                            rel="noreferrer"
                            href={siteURL('www', `/${kind}/${item.id}`)}
                          >
                            公开详情
                            <ArrowUpRight size={13} />
                          </a>
                        )}
                      {kind === 'service-packages' && (
                        <span className="muted small">由组织在工作台维护</span>
                      )}
                      {business && (
                        <Link className="directory-link" to={`/${kind}?organization_id=${id}`}>
                          查看管理记录
                        </Link>
                      )}
                      {!business && kind !== 'service-packages' && (
                        <Link
                          className="directory-link"
                          to={`/${kind}?organization_id=${id}&q=${encodeURIComponent(item.title)}`}
                        >
                          审核记录
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
      <Pagination
        page={page}
        size={pageSize}
        total={list.data?.total || 0}
        onPage={(n) => change({ page: String(n) }, false)}
      />
    </>
  );
}
